// ✅ THEME-ONLY UPDATE: TransportApprovalPanel -> match your latest Orange+Blue theme (same as AdminHostelPanel update)
// - Page BG wrapper + root outlined card
// - Orange gradient header bar
// - Section surface with LEFT orange stripe
// - Table header BLUE (like your latest themed pages)
// - Selects: blue outline + orange focus ring + orange focused label
// - LinearProgress orange bar
// - Alerts/Snackbar rounded + subtle blue border
// - Keeps ALL logic/APIs intact (StopID -> availableBuses mapping)

import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { API_URL } from "./config";

const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";

const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const TransportApprovalPanel = () => {
  const [applications, setApplications] = useState([]);
  const [availableBuses, setAvailableBuses] = useState({}); // { [StopID]: buses[] }
  const [logincampusCode, setCampusCode] = useState("");
  const [Username, setUsername] = useState("");

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [selectedBuses, setSelectedBuses] = useState({});
  const [stopIds, setStopIds] = useState([]);
  const [selectedBusStopMappingIDs, setSelectedBusStopMappingIDs] = useState({});

  const [loadingApps, setLoadingApps] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const token = localStorage.getItem("auth");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ===== Theme Sx =====
  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const sectionSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.75 },
  });

  const selectSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
    "& .MuiSelect-select": { py: 1.05 },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  // Fetch CampusCode & Username from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
      setUsername(decodedToken.username || decodedToken.Username || "");
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Failed to decode authentication token");
      setCampusCode("");
      setUsername("");
    }
  }, [token]);

  const fetchApplications = async () => {
    if (!logincampusCode) return;
    setError("");

    try {
      setLoadingApps(true);
      const response = await axios.get(`${API_URL}/api/pendingTransportApplications`, authHeader);

      const arr = Array.isArray(response.data) ? response.data : [];
      setApplications(arr);
      setPage(0);

      // Extract StopIDs from applications
      const extractedStopIds = [...new Set(arr.map((app) => app.StopID).filter(Boolean))];
      setStopIds(extractedStopIds);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError("Failed to fetch applications");
      setApplications([]);
      setStopIds([]);
    } finally {
      setLoadingApps(false);
    }
  };

  // Fetch Pending Transport Applications
  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode]);

  // Fetch Available Buses for needed StopIDs
  useEffect(() => {
    if (!logincampusCode || stopIds.length === 0) return;

    const fetchBuses = async () => {
      setError("");
      try {
        setLoadingBuses(true);

        const response = await axios.get(`${API_URL}/api/availableBuses`, {
          ...authHeader,
          params: { StopIDs: stopIds.join(",") },
        });

        const rows = Array.isArray(response.data) ? response.data : [];

        // Convert available buses into mapping by StopID
        const busesByStop = stopIds.reduce((acc, stopId) => {
          acc[stopId] = rows.filter((bus) => String(bus.StopID) === String(stopId));
          return acc;
        }, {});

        setAvailableBuses(busesByStop);
      } catch (err) {
        console.error("Error fetching buses:", err);
        setError("Failed to fetch available buses");
        setAvailableBuses({});
      } finally {
        setLoadingBuses(false);
      }
    };

    fetchBuses();
  }, [logincampusCode, stopIds, authHeader]);

  // Handle status change
  const handleStatusChange = (id, value) => {
    setSelectedStatuses((prev) => ({ ...prev, [id]: value }));
  };

  // Handle bus selection (also sets BusStopMappingID)
  const handleBusSelection = (id, busId, stopId) => {
    if (!availableBuses[stopId]) {
      console.error(`No available buses for StopID: ${stopId}`);
      return;
    }

    const busMapping = availableBuses[stopId].find((bus) => String(bus.BusID) === String(busId));
    const busStopMappingID = busMapping ? busMapping.BusStopMappingID : null;

    setSelectedBuses((prev) => ({ ...prev, [id]: busId }));
    setSelectedBusStopMappingIDs((prev) => ({ ...prev, [id]: busStopMappingID }));
  };

  // Approve/Reject + Assign bus
  const updateApplicationStatus = async (
    id,
    CampusCode,
    StudentID,
    StudentKey,
    StopID,
    AssignedBus,
    Status,
    UsernameParam
  ) => {
    try {
      setSavingId(id);
      setError("");

      const finalAssignedBus =
        selectedBuses[id] ||
        AssignedBus ||
        applications.find((app) => app.id === id)?.BusID ||
        "";

      const finalBusStopMappingID = selectedBusStopMappingIDs[id] || "";

      await axios.post(
        `${API_URL}/api/updateTransportApplicationStatus`,
        {
          id,
          StudentID,
          StudentKey,
          StopID,
          AssignedBus: finalAssignedBus,
          Status,
          Username: UsernameParam,
          BusStopMappingID: finalBusStopMappingID,
        },
        authHeader
      );

      setSuccess(true);

      // refresh list (and stopIds)
      await fetchApplications();

      // reflect status immediately
      setApplications((prev) => prev.map((app) => (app.id === id ? { ...app, Status } : app)));
      setSelectedStatuses((prev) => ({ ...prev, [id]: Status }));
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update status");
    } finally {
      setSavingId(null);
    }
  };

  const isBusy = loadingApps || loadingBuses;
  const paginatedApplications = useMemo(
    () => applications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [applications, page, rowsPerPage]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(applications.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [applications.length, rowsPerPage, page]);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Transport Applications Approver
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Assign bus and approve/reject applications
            </Typography>
          </Box>
        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              {error}
            </Alert>
          )}

          <Snackbar open={success} autoHideDuration={2500} onClose={() => setSuccess(false)}>
            <Alert
              severity="success"
              onClose={() => setSuccess(false)}
              sx={{ width: "100%", borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              Operation successful!
            </Alert>
          </Snackbar>

          {isBusy && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "rgba(25,118,210,0.10)",
                  "& .MuiLinearProgress-bar": { backgroundColor: THEME_ORANGE, borderRadius: 999 },
                }}
              />
            </Box>
          )}

          {/* ✅ Section (left stripe) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Pending Applications
              </Typography>
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  backgroundColor: "#fff",
                  overflow: "hidden",
                }}
              >
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                  <TableHead>
                    {/* ✅ Blue header row to match latest theme */}
                    <TableRow sx={theadRowSx}>
                      <TableCell sx={{ width: 60 }}>#</TableCell>
                      <TableCell>Sub Campus</TableCell>
                      <TableCell>Student ID</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Stop Name</TableCell>
                      <TableCell sx={{ minWidth: 260 }}>Assign Bus</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Status</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedApplications.map((app, index) => (
                      <TableRow key={app.id} hover sx={zebraRowSx(index)}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{app.SubCampusCode}</TableCell>
                        <TableCell>{app.StudentID}</TableCell>
                        <TableCell>{app.FullName}</TableCell>
                        <TableCell>{app.StopName}</TableCell>

                        {/* Assign Bus */}
                        <TableCell>
                          <Select
                            size="small"
                            sx={selectSx}
                            value={selectedBuses[app.id] || ""}
                            onChange={(e) => handleBusSelection(app.id, e.target.value, app.StopID)}
                            fullWidth
                          >
                            <MenuItem value="">Select Bus</MenuItem>
                            {(availableBuses[app.StopID] || []).map((bus) => (
                              <MenuItem key={bus.BusID} value={bus.BusID}>
                                {bus.BusNumber} ({bus.DriverName}) — Cap:{bus.Capacity} Occ:{bus.Occupied}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Select
                            size="small"
                            sx={selectSx}
                            value={selectedStatuses[app.id] || app.Status || "Pending"}
                            onChange={(e) => handleStatusChange(app.id, e.target.value)}
                            fullWidth
                          >
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                          </Select>
                        </TableCell>

                        {/* Save */}
                        <TableCell>
                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            onClick={() =>
                              updateApplicationStatus(
                                app.id,
                                app.CampusCode,
                                app.StudentID,
                                app.StudentKey,
                                app.StopID,
                                selectedBuses[app.id] || "",
                                selectedStatuses[app.id] || app.Status,
                                Username
                              )
                            }
                            disabled={savingId === app.id}
                          >
                            {savingId === app.id ? (
                              <CircularProgress size={18} sx={{ color: "white" }} />
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {!loadingApps && applications.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
                          No pending transport applications.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                component="div"
                count={applications.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
                labelRowsPerPage="Rows per page"
                showFirstButton
                showLastButton
                sx={{
                  borderTop: `1px solid ${BORDER}`,
                  backgroundColor: "rgba(25,118,210,.025)",
                  "& .MuiTablePagination-toolbar": { minHeight: 64 },
                }}
              />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default TransportApprovalPanel;
